import { createPortal } from "react-dom";
import { FloatingControlPanel } from "../floating-control";
import { isPipSupported, openPipWindow, usePipWindowStore } from "./pip-window";
import { RecordingCard } from "./recording-card";
import { RecordingPanel } from "./recording-panel";
import { useRecordingElapsed } from "./use-recording-elapsed";

/** How long before the limit the panel starts counting down. */
const LIMIT_WARNING_MS = 2 * 60 * 1000;
import { useVideoRecorder } from "./use-video-recorder";

/** Props Interface */
interface RecordingPanelHostProps {
	/**
	 * Takes the author back to where the recording belongs. Supplied by the host because this
	 * package knows the destination only as a link, not as a route.
	 */
	onOpenTarget: (href: string) => void;
}

/**
 * Mounts the recording surface once for the whole application and shows it only when there is
 * something to steer. Mounted unconditionally, rendering nothing while idle — the same shape
 * the local dev sign-in picker uses.
 *
 * Three states, and only the middle one involves a running capture: nothing at all while idle,
 * the panel while recording or paused, and the card from the moment the recording stops until
 * the author dismisses it. The card is the only state visible without a recording, and it has
 * to be — otherwise nobody would know the upload is still running.
 *
 * **The same surface has two possible hosts, and the author picks.** By default it renders in
 * the page, where it is laid out the way everything else in the app is. The panel offers to move
 * into a floating window that stays visible after switching to another program — which is what
 * makes recording-while-working possible, and which is a window the author opens deliberately
 * rather than one that appears at every start and is in the way of everyone who never leaves the
 * browser. Closing it brings the panel back into the page. Safari, mobile and the desktop build
 * have no such window and never offer it.
 *
 * In the page it is portalled to `document.body` as a direct child rather than rendered inside
 * the application root, and that is load-bearing: Headless UI marks the root `inert` while any
 * dialog is open, which would leave Stop unclickable for as long as a modal stands somewhere in
 * the app. Body-level siblings are exempt from that, and Headless UI also treats them as
 * allowed containers, so clicking here does not dismiss the dialog underneath.
 *
 * There is no start control here on purpose. A recording begins where the video belongs — the
 * record button in the wiki editor — because only that place knows which page the placeholder
 * block is anchored to.
 */
export function RecordingPanelHost({ onOpenTarget }: RecordingPanelHostProps) {
	/** Store */
	const {
		isRecording,
		isPaused,
		isAssembling,
		isUploading,
		isUploaded,
		hasFailed,
		target,
		progress,
		onPauseRecording,
		onResumeRecording,
		onStopRecording,
		onRetryUpload,
		onReset,
		didReachLimit,
	} = useVideoRecorder();
	const pipWindow = usePipWindowStore((state) => state.pipWindow);

	/** Hooks */
	const { elapsed, remaining, remainingMs } = useRecordingElapsed();

	/** Computed */
	const showsCard = (isAssembling || isUploading || isUploaded || hasFailed) && !!target;

	/** Guard */
	if (!isRecording && !showsCard) return null;

	/** Render */
	const surface = isRecording ? (
		<RecordingPanel
			isPaused={isPaused}
			// Offered only in the page, and only where a floating window exists to move into.
			onUndock={!pipWindow && isPipSupported() ? () => void openPipWindow() : undefined}
			elapsed={elapsed}
			// Only once it is close: a countdown running for half an hour is noise, and the last
			// two minutes are when it becomes information.
			remaining={remainingMs <= LIMIT_WARNING_MS ? remaining : undefined}
			onPause={onPauseRecording}
			onResume={onResumeRecording}
			onStop={onStopRecording}
		/>
	) : (
		target && (
			<RecordingCard
				label={target.label}
				progress={progress}
				// Assembling the container is part of the same wait as the transfer, and telling
				// them apart would only name a distinction the author cannot act on.
				isUploading={isAssembling || isUploading}
				hasFailed={hasFailed}
				didReachLimit={didReachLimit}
				onOpenTarget={() => {
					onOpenTarget(target.href);
					onReset();
				}}
				onRetry={onRetryUpload}
				onDismiss={onReset}
			/>
		)
	);

	if (pipWindow) {
		// Two jobs, deliberately split. The outer surface fills the window and paints it, so a
		// window the author drags larger has no bare corner. The inner one hugs what it holds and
		// is what the window measures itself against — tagged, because the window is opened before
		// React puts anything in it.
		return createPortal(
			<div className="flex-center w-full h-full bg-primary-50 dark:bg-primary-950">
				<div
					data-pip-content
					className="w-max p-2"
				>
					{surface}
				</div>
			</div>,
			pipWindow.document.body,
		);
	}

	// The app's floating shell rather than a corner of our own: it brings dragging, corner
	// snapping and sliding off the edge, which a recording of the full screen wants — the panel
	// is in the picture there, and the author may need it out of the way. It also portals onto
	// the body and sits above the modal layer, which is what keeps a recording started from a
	// dialog steerable while the dialog is open.
	return <FloatingControlPanel>{surface}</FloatingControlPanel>;
}
